import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import api from "@/lib/api";
import { AuthProvider } from "@/context/AuthContext";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Home from "@/pages/Home";
import Work from "@/pages/Work";
import ProjectDetail from "@/pages/ProjectDetail";
import About from "@/pages/About";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminPortfolio from "@/pages/admin/AdminPortfolio";
import PortfolioForm from "@/pages/admin/PortfolioForm";
import AdminBlog from "@/pages/admin/AdminBlog";
import BlogForm from "@/pages/admin/BlogForm";
import MediaLibrary from "@/pages/admin/MediaLibrary";
import Testimonials from "@/pages/admin/Testimonials";
import Analytics from "@/pages/admin/Analytics";
import Settings from "@/pages/admin/Settings";

function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    let v = localStorage.getItem("jare_vid");
    if (!v) {
      v = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("jare_vid", v);
    }
    api.post("/analytics/track", { path: pathname, visitor: v, referrer: document.referrer }).catch(() => {});
  }, [pathname]);
  return null;
}

function CircleCursor() {
  const ref = useRef(null);
  useEffect(() => {
    let tx = -100, ty = -100, cx = tx, cy = ty, raf;
    const move = (e) => {
      tx = e.clientX;
      ty = e.clientY;
    };
    const over = (e) => {
      const interactive = e.target.closest?.("a, button, input, textarea, select, [contenteditable='true']");
      ref.current?.classList.toggle("circle-cursor-active", Boolean(interactive));
    };
    const loop = () => {
      cx += (tx - cx) * 0.18;
      cy += (ty - cy) * 0.18;
      if (ref.current) ref.current.style.transform = `translate(${cx}px, ${cy}px) translate(-50%, -50%)`;
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("mouseover", over, { passive: true });
    raf = requestAnimationFrame(loop);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      cancelAnimationFrame(raf);
    };
  }, []);
  return <div ref={ref} className="circle-cursor" data-testid="circle-cursor" aria-hidden="true" />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const Site = ({ children }) => (
  <div className="site-cursor min-h-screen bg-[#050505] text-white antialiased">
    <CircleCursor />
    <AnalyticsTracker />
    <Navbar />
    <main>{children}</main>
    <Footer />
  </div>
);

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    window.lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster
          theme="dark"
          position="top-center"
          toastOptions={{
            style: {
              background: "#0B0B0B",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "#fff",
            },
          }}
        />
        <div className="grain-overlay" aria-hidden="true" />
        <div className="scroll-fade scroll-fade-top" aria-hidden="true" />
        <div className="scroll-fade scroll-fade-bottom" aria-hidden="true" />
        <Routes>
          <Route path="/" element={<Site><Home /></Site>} />
          <Route path="/work" element={<Site><Work /></Site>} />
          <Route path="/work/:slug" element={<Site><ProjectDetail /></Site>} />
          <Route path="/about" element={<Site><About /></Site>} />
          <Route path="/blog" element={<Site><Blog /></Site>} />
          <Route path="/blog/:slug" element={<Site><BlogPost /></Site>} />
          <Route path="/contact" element={<Site><Contact /></Site>} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Dashboard />} />
            <Route path="portfolio" element={<AdminPortfolio />} />
            <Route path="portfolio/new" element={<PortfolioForm />} />
            <Route path="portfolio/:id/edit" element={<PortfolioForm />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="blog/new" element={<BlogForm />} />
            <Route path="blog/:id/edit" element={<BlogForm />} />
            <Route path="media" element={<MediaLibrary />} />
            <Route path="testimonials" element={<Testimonials />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="settings" element={<Settings />} />
          </Route>
          <Route path="*" element={<Site><Home /></Site>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
